import {
    CheckCircleFilled,
    CloseOutlined,
    CustomerServiceOutlined,
    FileTextOutlined,
    LockOutlined,
    ThunderboltFilled,
    UploadOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Upload, message } from 'antd';
import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';
import {
    ACCEPT_EXTENSIONS,
    FORMAT_PRICES,
    MAX_FILE_MB,
    createFormatOrder,
    errorText,
    fileExtension,
    formatSize,
    formatYuan,
    getFormatOrder,
    rememberOrder,
    rememberedOrder,
} from './api';
import FormatPayModal from './components/FormatPayModal';
import PaidModal from './components/PaidModal';
import styles from './index.module.scss';

const { Dragger } = Upload;

/** 本机记住的订单多久内还在页面上提示：待支付 1 天，已支付 30 天 */
const PENDING_REMEMBER_MS = 24 * 3600 * 1000;
const PAID_REMEMBER_MS = 30 * 24 * 3600 * 1000;

const FEATURES = [
    '文字格式（字体、字号、间距等）',
    '图、表、公式题注引用',
    '参考文献交叉引用',
    '目录、页眉、页脚、奇偶页',
    '中英文符号、多余空格',
    '封面、摘要、附录、致谢',
];

const PROMISES = ['售后陪伴', '全程保密', '官方担保', '无需担忧'];

const STEPS = [
    { title: '填写学校、上传原文', desc: '支持 doc / docx / pdf' },
    { title: '扫码支付', desc: `普通 ¥${FORMAT_PRICES.normal} · 加急 ¥${FORMAT_PRICES.rush}` },
    { title: '添加客服', desc: '发送订单号，确认格式要求' },
    { title: '领取精修文件', desc: '普通 48 小时 · 加急 24 小时内' },
];

const SERVICE_OPTIONS = [
    { rush: false, label: '普通', price: FORMAT_PRICES.normal, desc: '48 小时内交付' },
    { rush: true, label: '加急', price: FORMAT_PRICES.rush, desc: '24 小时内交付', badge: '更快' },
];

/** 普通 / 加急单选卡片（受控：value 为是否加急） */
function ServiceOptions({ value = false, onChange }) {
    return (
        <div className={styles.options} role="radiogroup" aria-label="服务类型">
            {SERVICE_OPTIONS.map((item) => {
                const active = value === item.rush;
                return (
                    <button
                        key={item.label}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={cx(styles.option, active && styles.optionActive)}
                        onClick={() => onChange?.(item.rush)}
                    >
                        <span className={styles.optionHead}>
                            <span className={styles.optionLabel}>
                                {item.rush ? <ThunderboltFilled /> : null}
                                {item.label}
                            </span>
                            {item.badge ? <span className={styles.optionBadge}>{item.badge}</span> : null}
                            <span className={styles.optionCheck}>
                                {active ? <CheckCircleFilled /> : null}
                            </span>
                        </span>
                        <span className={styles.optionPrice}>
                            ¥<b>{item.price}</b>
                            <small>/篇</small>
                        </span>
                        <span className={styles.optionDesc}>{item.desc}</span>
                    </button>
                );
            })}
        </div>
    );
}

const orderSignature = (values, file) =>
    [
        (values.schoolName || '').trim(),
        values.rush ? 1 : 0,
        (values.contact || '').trim(),
        (values.remark || '').trim(),
        file?.uid || '',
    ].join('|');

const Formatter = () => {
    const [form] = Form.useForm();
    const rush = Form.useWatch('rush', form) || false;
    const [file, setFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [progress, setProgress] = useState(0);
    // 已创建、尚未付款的订单（关闭付款弹窗后可继续支付，内容未改时不重复下单）
    const [pendingOrder, setPendingOrder] = useState(null);
    const [payOpen, setPayOpen] = useState(false);
    const [paidOrder, setPaidOrder] = useState(null);
    const [paidOpen, setPaidOpen] = useState(false);
    const formRef = useRef(null);

    // 本机记住的最近一笔订单：已支付 → 可再次打开客服二维码；待支付 → 回到页面后确认是否已到账（手机端跳转付款）
    useEffect(() => {
        const saved = rememberedOrder();
        if (!saved) return undefined;
        const age = Date.now() - (Number(saved.savedAt) || 0);
        if (age > (saved.paid ? PAID_REMEMBER_MS : PENDING_REMEMBER_MS)) {
            rememberOrder(null);
            return undefined;
        }
        if (saved.paid) {
            setPaidOrder(saved);
            return undefined;
        }
        let alive = true;
        getFormatOrder(saved.orderNo)
            .then((status) => {
                if (!alive) return;
                if (status?.paid) {
                    const paid = { ...saved, paid: true };
                    rememberOrder(paid);
                    setPaidOrder(paid);
                    setPaidOpen(true);
                } else if (status) {
                    setPendingOrder(saved);
                }
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, []);

    const scrollToForm = () => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const beforeUpload = (picked) => {
        const ext = fileExtension(picked.name);
        if (!ACCEPT_EXTENSIONS.includes(ext)) {
            message.warning('仅支持上传 Word（.doc / .docx）或 PDF 文件');
            return Upload.LIST_IGNORE;
        }
        if (picked.size > MAX_FILE_MB * 1024 * 1024) {
            message.warning(`文件不能超过 ${MAX_FILE_MB}MB`);
            return Upload.LIST_IGNORE;
        }
        if (picked.size === 0) {
            message.warning('文件内容为空，请重新选择');
            return Upload.LIST_IGNORE;
        }
        setFile(picked);
        setFileError('');
        // 只在本地暂存，点「提交并支付」时与表单一起提交
        return false;
    };

    const handlePaid = () => {
        const order = pendingOrder;
        if (!order) return;
        const paid = { ...order, paid: true };
        rememberOrder(paid);
        setPayOpen(false);
        setPendingOrder(null);
        setPaidOrder(paid);
        setPaidOpen(true);
        setFile(null);
        form.resetFields();
        message.success('支付成功');
    };

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            if (!file) setFileError('请上传论文原文');
            scrollToForm();
            return;
        }
        if (!file) {
            setFileError('请上传论文原文');
            scrollToForm();
            return;
        }
        const signature = orderSignature(values, file);
        if (pendingOrder && pendingOrder.signature === signature) {
            setPayOpen(true);
            return;
        }
        setSubmitting(true);
        setProgress(0);
        try {
            const schoolName = values.schoolName.trim();
            const res = await createFormatOrder(
                {
                    file,
                    schoolName,
                    rush: !!values.rush,
                    remark: (values.remark || '').trim(),
                    contact: (values.contact || '').trim(),
                },
                setProgress,
            );
            if (!res?.orderNo) throw new Error('提交订单失败，请重试');
            const order = {
                orderNo: res.orderNo,
                payNo: res.payNo || res.orderNo,
                price: res.price,
                rush: !!res.rush,
                schoolName,
                fileName: file.name,
                signature,
            };
            setPendingOrder(order);
            rememberOrder(order);
            setPayOpen(true);
        } catch (error) {
            message.error(errorText(error, '提交订单失败，请重试'));
        } finally {
            setSubmitting(false);
        }
    };

    const price = rush ? FORMAT_PRICES.rush : FORMAT_PRICES.normal;
    let submitText = '提交并支付';
    if (submitting) submitText = progress > 0 ? `上传中 ${progress}%` : '提交中…';

    return (
        <div className={styles.page}>
            <div className={styles.bg} aria-hidden="true" />

            <section className={styles.hero}>
                <p className={styles.kicker}>论文格式难搞定？小灵人工精修帮您轻松解决！</p>
                <h1 className={styles.heroTitle}>
                    一键上传原文，<span>最快 24 小时</span>获取完美格式
                </h1>
                <p className={styles.heroSub}>
                    填写学校名称、上传原文、扫码支付，添加客服即可领取精修后的文件
                </p>
                <Button
                    type="primary"
                    shape="round"
                    size="large"
                    className={styles.heroBtn}
                    icon={<UploadOutlined />}
                    onClick={scrollToForm}
                >
                    立即上传
                </Button>
            </section>

            <section className={styles.banner}>
                <div className={styles.bannerMain}>
                    <h2 className={styles.bannerTitle}>
                        官方<em>人工精修</em>论文格式
                    </h2>
                    <ul className={styles.promises}>
                        {PROMISES.map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                    <ul className={styles.features}>
                        {FEATURES.map((item) => (
                            <li key={item}>
                                <CheckCircleFilled />
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className={styles.bannerArt} aria-hidden="true">
                    <div className={styles.paper}>
                        <FileTextOutlined className={styles.paperIcon} />
                        <span className={styles.paperLine} />
                        <span className={styles.paperLine} />
                        <span className={cx(styles.paperLine, styles.paperLineShort)} />
                        <span className={styles.paperLine} />
                        <span className={cx(styles.paperLine, styles.paperLineShort)} />
                    </div>
                    <span className={cx(styles.chip, styles.chipA)}>
                        <CheckCircleFilled /> 字体字号
                    </span>
                    <span className={cx(styles.chip, styles.chipB)}>
                        <CheckCircleFilled /> 目录页眉
                    </span>
                    <span className={cx(styles.chip, styles.chipC)}>
                        <CheckCircleFilled /> 参考文献
                    </span>
                </div>
            </section>

            <section ref={formRef} className={styles.card}>
                <div className={styles.cardHead}>
                    <h2 className={styles.cardTitle}>提交格式精修订单</h2>
                    <p className={styles.cardSub}>无需登录，付款后添加客服，凭订单号领取精修文件</p>
                </div>

                {paidOrder && !pendingOrder ? (
                    <div className={styles.notice}>
                        <span>
                            订单 <b>{paidOrder.orderNo}</b> 已支付，添加客服并发送订单号即可领取文件
                        </span>
                        <Button
                            size="small"
                            type="primary"
                            ghost
                            icon={<CustomerServiceOutlined />}
                            onClick={() => setPaidOpen(true)}
                        >
                            联系客服
                        </Button>
                    </div>
                ) : null}
                {pendingOrder ? (
                    <div className={styles.notice}>
                        <span>
                            订单 <b>{pendingOrder.orderNo}</b> 待支付
                        </span>
                        <Button size="small" type="primary" ghost onClick={() => setPayOpen(true)}>
                            继续支付
                        </Button>
                    </div>
                ) : null}

                <Form
                    form={form}
                    layout="vertical"
                    requiredMark
                    initialValues={{ rush: false }}
                    className={styles.form}
                >
                    <Form.Item
                        label="学校名称"
                        name="schoolName"
                        rules={[
                            { required: true, whitespace: true, message: '请输入学校名称' },
                            { max: 100, message: '学校名称不能超过 100 个字' },
                        ]}
                    >
                        <Input
                            size="large"
                            maxLength={100}
                            placeholder="请输入学校全称，如：XX大学"
                            autoComplete="organization"
                        />
                    </Form.Item>

                    <Form.Item
                        label="上传原文"
                        required
                        validateStatus={fileError ? 'error' : ''}
                        help={fileError || undefined}
                    >
                        {file ? (
                            <div className={styles.file}>
                                <FileTextOutlined className={styles.fileIcon} />
                                <div className={styles.fileMeta}>
                                    <span className={styles.fileName} title={file.name}>
                                        {file.name}
                                    </span>
                                    <span className={styles.fileSize}>{formatSize(file.size)}</span>
                                </div>
                                <Upload
                                    accept=".doc,.docx,.pdf"
                                    showUploadList={false}
                                    beforeUpload={beforeUpload}
                                    disabled={submitting}
                                >
                                    <Button size="small" disabled={submitting}>
                                        重新选择
                                    </Button>
                                </Upload>
                                <button
                                    type="button"
                                    className={styles.fileRemove}
                                    aria-label="移除文件"
                                    disabled={submitting}
                                    onClick={() => setFile(null)}
                                >
                                    <CloseOutlined />
                                </button>
                            </div>
                        ) : (
                            <Dragger
                                accept=".doc,.docx,.pdf"
                                multiple={false}
                                showUploadList={false}
                                beforeUpload={beforeUpload}
                                className={styles.dragger}
                            >
                                <div className={styles.drop}>
                                    <UploadOutlined className={styles.dropIcon} />
                                    <div className={styles.dropTitle}>点击或拖拽论文原文到此区域</div>
                                    <div className={styles.dropHint}>
                                        支持 doc、docx、pdf，大小不超过 {MAX_FILE_MB}MB
                                    </div>
                                </div>
                            </Dragger>
                        )}
                    </Form.Item>

                    <Form.Item label="服务类型" name="rush" required>
                        <ServiceOptions />
                    </Form.Item>

                    <div className={styles.optionalRow}>
                        <Form.Item
                            label="联系方式（选填）"
                            name="contact"
                            rules={[{ max: 64, message: '联系方式不能超过 64 个字' }]}
                        >
                            <Input maxLength={64} placeholder="手机号或微信号，方便客服联系您" />
                        </Form.Item>
                        <Form.Item
                            label="备注（选填）"
                            name="remark"
                            rules={[{ max: 500, message: '备注不能超过 500 个字' }]}
                        >
                            <Input.TextArea
                                maxLength={500}
                                autoSize={{ minRows: 1, maxRows: 4 }}
                                placeholder="特殊格式要求，如学院模板、截止时间等"
                            />
                        </Form.Item>
                    </div>
                </Form>

                <div className={styles.submitBar}>
                    <div className={styles.amount}>
                        <span>应付</span>
                        <b>¥{formatYuan(price)}</b>
                        <small>{rush ? '加急 · 24 小时内交付' : '普通 · 48 小时内交付'}</small>
                    </div>
                    <Button
                        type="primary"
                        size="large"
                        shape="round"
                        className={styles.submitBtn}
                        loading={submitting}
                        onClick={handleSubmit}
                    >
                        {submitText}
                    </Button>
                </div>
                <p className={styles.privacy}>
                    <LockOutlined />
                    格式精修需要查看您的文档内容，我们严格保障文件安全，绝不泄露、篡改或滥用
                </p>
            </section>

            <section className={styles.steps}>
                <h2 className={styles.stepsTitle}>服务流程</h2>
                <ol className={styles.stepList}>
                    {STEPS.map((step, index) => (
                        <li key={step.title} className={styles.step}>
                            <span className={styles.stepNo}>{index + 1}</span>
                            <span className={styles.stepTitle}>{step.title}</span>
                            <span className={styles.stepDesc}>{step.desc}</span>
                        </li>
                    ))}
                </ol>
            </section>

            <FormatPayModal
                open={payOpen && !!pendingOrder}
                order={pendingOrder}
                onClose={() => setPayOpen(false)}
                onPaid={handlePaid}
            />
            <PaidModal
                open={paidOpen && !!paidOrder}
                orderNo={paidOrder?.orderNo}
                rush={paidOrder?.rush}
                onClose={() => setPaidOpen(false)}
            />
        </div>
    );
};

export default Formatter;
